import useKanbanColumnMutation, {
  KanbanColumnType,
} from "@/api/mutation/useKanbanCreateColumn";
import ErrorMessage from "@/components/common/ErrorMessage";
import InputText from "@/components/common/InputText";
import InputNumber from "@/components/common/InputNumber";
import { Button } from "@/components/ui/button";
import { createKanbanSchema } from "@backend/src/schemas/projectSchema";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { errorConfig } from "@/lib/toastError";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import InfoCircle from "@/assets/svg/Info circle.svg";
import FormLabel from "@/components/common/FormLabel";
import Plus from "@/assets/svg/NoramlPlus.svg";
type Props = {
  projectId: string;
  refetch?: () => void;
  close?: () => void;
  disabled?: boolean;
  rules?: KanbanColumnType[];
  className: string
  isProjectEditBlocked?: boolean
  setIsProjectEditBlockedDialogOpen?: (data: boolean) => void;
};

function RulesForm(props: Props) {
  const { t } = useTranslation();
  const kanbanColumnMutation = useKanbanColumnMutation(props.projectId);
  const kanbanColumnFormik = useFormik<z.infer<typeof createKanbanSchema>>({
    initialValues: {
      name: "",
    },
    validationSchema: toFormikValidationSchema(createKanbanSchema),
    onSubmit: (values) => {
      let data: z.infer<typeof createKanbanSchema> = { name: values.name };
      if (setPercentage(values.percentage, values.name)) {
        if (typeof values.percentage == "number") {
          data = { ...data, percentage: values.percentage };
        }
        kanbanColumnMutation.mutate(data, {
          onSuccess() {
            kanbanColumnFormik.resetForm();
            if (props?.close) {
              props?.close();
            }
            if (props.refetch) {
              props.refetch();
            }
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig
            );
          },
        });
      }
    },
  });
  const handleSave = () => {
    kanbanColumnFormik.submitForm();
  };
  const setPercentage = (value: number | undefined | null, name?: string | undefined | null) => {
    if (!value) {
      return true;
    } else if (
      name &&
      props.rules?.some((d) => d.percentage === Number(value) && d.name == name)
    ) {
      kanbanColumnFormik.setErrors({
        percentage: "Rule already exists. Choose a unique one.",
      });
      return false;
    } else if (value > 100) {
      kanbanColumnFormik.setErrors({
        percentage: "Percentage cannot be greater than 100%",
      });
      return false;
    } else {
      return true;
    }
  };

  return (
    <div className={`relative flex flex-col justify-between gap-2  p-4 ${props.className}`}>
      <div>
        <FormLabel className="text-sm   !text-black mb-2">{t("Column Name")}</FormLabel>
        <InputText
          name="name"
          className=" rounded-md mt-1 bg-[#FFFFFF] text-sm"
          placeholder={t("column_name")}
          disabled={props.disabled}
          onChange={kanbanColumnFormik.handleChange}
          value={kanbanColumnFormik.values.name}
        />
        {kanbanColumnFormik.errors.name && <ErrorMessage className="text-xs">
          {t(kanbanColumnFormik.errors.name ?? "")}
        </ErrorMessage>}
      </div>
      <div>
        <FormLabel className="text-sm  !text-black mb-2">
          {t("Task Percentage")}
        </FormLabel>
        <div className="flex items-center gap-2 relative">
          <InputNumber
            className=" rounded-md mt-1  bg-[#FFFFFF] text-sm"
            name="percentage"
            min={0}
            max={100}
            disabled={props.disabled}
            placeholder={t("task_percentage")}
            onChange={(e) => {
              kanbanColumnFormik.handleChange(e);
              setPercentage(Number(e.target.value));
            }}
            value={kanbanColumnFormik.values.percentage ?? ""}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="flex items-center justify-end absolute right-2 ">
                <img
                  src={InfoCircle}
                  className="h-[16px] w-[16px] brightness-0 opacity-50"
                  alt="InfoCircle"
                />
              </TooltipTrigger>
              <TooltipContent className="absolute top-36">
                <p className="text-sm w-[300px]">
                  {t("Please set the progress limit from which a task will be moved to this column")}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {kanbanColumnFormik.errors.percentage && <ErrorMessage className="text-xs">
          {t(kanbanColumnFormik.errors.percentage ?? "")}
        </ErrorMessage>}
      </div>
      <div className="w-full">
        <Button
          size={"sm"}
          className="w-full mt-2 "
          variant={"primary"}
          isLoading={kanbanColumnMutation.isPending}
          disabled={kanbanColumnMutation.isPending || props.disabled}
          onClick={() => {
            if (props.isProjectEditBlocked) {
              props.setIsProjectEditBlockedDialogOpen && props.setIsProjectEditBlockedDialogOpen(true);
              return;
            }
            handleSave();
          }}
        >
          <img src={Plus} className="w-3 min-w-3 mr-1" />
          {t("add_column")}
        </Button>
      </div>
    </div>
  );
}

export default RulesForm;
